/**
 * Session-only PDF open-password protection (applied on download).
 * Password is held in a module ref — never persisted to Zustand / storage.
 */

import { PDFDocument, PDFHeader } from '@cantoo/pdf-lib';

export type PdfEncryptionAlgorithm = 'aes-128' | 'aes-256';

export interface PdfProtectionSettings {
  enabled: boolean;
  algorithm: PdfEncryptionAlgorithm;
}

export interface PdfProtectionEncryptOptions {
  password: string;
  algorithm: PdfEncryptionAlgorithm;
}

const MIN_PASSWORD_LENGTH = 4;

/** In-memory open password for the current PDF Studio session */
let sessionPassword: string | null = null;

export function setProtectionPassword(password: string | null): void {
  sessionPassword = password && password.length > 0 ? password : null;
}

export function getProtectionPassword(): string | null {
  return sessionPassword;
}

export function clearProtectionPassword(): void {
  sessionPassword = null;
}

export function validateProtectionPasswords(
  password: string,
  confirm: string
): string | null {
  if (!password) return 'Enter a password';
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
  }
  if (password !== confirm) return 'Passwords do not match';
  return null;
}

/**
 * Set PDF header version so @cantoo/pdf-lib picks AES-128 (V=4) or AES-256 (V=5).
 * See PDFSecurity.initialize — '1.7' → AES-128, '1.7ext3' → AES-256.
 */
function setEncryptionHeader(
  pdfDoc: PDFDocument,
  algorithm: PdfEncryptionAlgorithm
): void {
  // cantoo PDFSecurity picks cipher from header version string:
  // '1.7' → AES-128 (V=4), '1.7ext3' → AES-256 (V=5)
  pdfDoc.context.header =
    algorithm === 'aes-256'
      ? PDFHeader.forVersion(1, '7ext3' as never)
      : PDFHeader.forVersion(1, 7);
}

/** Apply open-password encryption to an in-memory PDFDocument (before save). */
export function applyPdfEncryption(
  pdfDoc: PDFDocument,
  options: PdfProtectionEncryptOptions
): void {
  const password = options.password;
  if (!password) {
    throw new Error('Password is required to encrypt the PDF');
  }
  setEncryptionHeader(pdfDoc, options.algorithm);
  pdfDoc.encrypt({
    userPassword: password,
    ownerPassword: password,
  });
}

/** Load bytes, encrypt, return encrypted PDF bytes. */
export async function encryptPdfBytes(
  bytes: Uint8Array,
  options: PdfProtectionEncryptOptions
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.load(bytes);
  applyPdfEncryption(pdfDoc, options);
  const encrypted = await pdfDoc.save();
  const copy = new Uint8Array(encrypted.byteLength);
  copy.set(encrypted);
  return copy;
}
